import React, { useState, useEffect } from 'react';
import {
    Phone, PhoneOff, Volume2, UserCircle, Clock, Activity, RefreshCcw, Radio, Mic
} from 'lucide-react';
import { useActiveCalls } from '../../hooks/useVoiceAPI';
import { formatDuration, formatPhoneNumber } from '../../lib/utils';
import { useBranding } from '../../context/BrandingContext';

// --- INTERNAL COMPONENTS (Robust, No External Deps) ---

const Card = ({ children, className = "" }) => (
    <div className={`bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden ${className}`}>
        {children}
    </div>
);

const Badge = ({ children, variant = 'neutral', className = "" }) => {
    const variants = {
        neutral: "bg-slate-100 text-slate-600 border-slate-200",
        success: "bg-emerald-50 text-emerald-700 border-emerald-200",
        warning: "bg-amber-50 text-amber-700 border-amber-200",
        error: "bg-red-50 text-red-700 border-red-200",
        info: "bg-blue-50 text-blue-700 border-blue-200",
    };
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${variants[variant] || variants.neutral} ${className}`}>
            {children}
        </span>
    );
};

const Button = ({ children, variant = "primary", size = "default", onClick, disabled, className = "" }) => {
    const base = "inline-flex items-center justify-center rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";
    const variants = {
        primary: "bg-blue-600 text-white hover:bg-blue-700",
        secondary: "bg-slate-100 text-slate-900 hover:bg-slate-200",
        outline: "border border-slate-200 bg-white hover:bg-slate-50 text-slate-700",
        ghost: "hover:bg-slate-100 text-slate-600",
        destructive: "bg-white border border-red-200 text-red-600 hover:bg-red-50",
    };
    const sizes = {
        default: "h-9 px-4 py-2",
        sm: "h-8 px-3 text-xs",
        icon: "h-9 w-9",
    };
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`${base} ${variants[variant] || variants.primary} ${sizes[size] || sizes.default} ${className}`}
        >
            {children}
        </button>
    );
};

// --- MAIN COMPONENT ---

const LiveCallMonitor = ({ profileId }) => {
    const brandingContext = useBranding();
    const isFitenko = Boolean(
        brandingContext?.isFitenko ||
        brandingContext?.branding?.instance_name === 'fitenko' ||
        (typeof window !== 'undefined' && (
            window.location.hostname.includes('fitenko') ||
            localStorage.getItem('tenant_id') === 'fitenko' ||
            localStorage.getItem('lastTenant') === 'fitenko'
        ))
    );

    const { activeCalls, loading, refetch, terminateCall, transferCall } = useActiveCalls(profileId);
    const [selectedCall, setSelectedCall] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [initialLoad, setInitialLoad] = useState(true);

    // Effect to handle initial loading state
    useEffect(() => {
        if (!loading) {
            setInitialLoad(false);
        }
    }, [loading]);

    // Default to first call if none selected
    const currentCall = activeCalls?.find(call => call.id === selectedCall) || activeCalls?.[0];

    // Timer Logic
    useEffect(() => {
        if (!currentCall?.created_at) {
            setElapsedTime(0);
            return;
        }
        const updateTimer = () => {
            const start = new Date(currentCall.created_at).getTime();
            const now = new Date().getTime();
            const seconds = Math.floor((now - start) / 1000);
            setElapsedTime(seconds > 0 ? seconds : 0);
        };
        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [currentCall?.created_at]);

    // Helpers
    const getSentimentVariant = (sentiment) => {
        switch (sentiment?.toLowerCase()) {
            case 'positive': return 'success';
            case 'neutral': return 'info';
            case 'negative': return 'warning';
            case 'angry': return 'error';
            default: return 'neutral';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'ringing': return <Phone className={`h-4 w-4 ${isFitenko ? 'text-[#C8A653]' : 'text-blue-500'} animate-pulse`} />;
            case 'in-progress': return <Activity className="h-4 w-4 text-emerald-500" />;
            case 'on-hold': return <Volume2 className="h-4 w-4 text-amber-500" />;
            default: return <Phone className="h-4 w-4 text-slate-400" />;
        }
    };

    // --- RENDER STATES ---

    // ONLY show full spinner on INITIAL load
    if (initialLoad && loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className={`w-8 h-8 border-4 border-slate-200 ${isFitenko ? 'border-t-[#C8A653]' : 'border-t-blue-500'} rounded-full animate-spin`}></div>
            </div>
        );
    }

    if (!activeCalls || activeCalls.length === 0) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '28rem',
                    borderRadius: '0.75rem',
                    border: isFitenko ? '1px dashed #C8A653' : '2px dashed #e2e8f0',
                    backgroundColor: isFitenko ? 'rgba(250, 244, 229, 0.35)' : '#f8fafc',
                    padding: '2rem'
                }}
            >
                <div style={{
                    padding: '1.5rem',
                    borderRadius: '9999px',
                    backgroundColor: isFitenko ? '#FAF4E5' : '#f1f5f9',
                    marginBottom: '1.25rem',
                    border: isFitenko ? '1px solid #E5E1D6' : 'none'
                }}>
                    <PhoneOff size={44} color={isFitenko ? '#C8A653' : '#94a3b8'} />
                </div>
                <p style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: isFitenko ? '#20231F' : '#0f172a',
                    fontFamily: isFitenko ? 'DM Serif Display, Georgia, serif' : 'inherit',
                    margin: 0
                }}>
                    No Active Calls
                </p>
                <p style={{
                    fontSize: '0.875rem',
                    color: isFitenko ? '#687077' : '#64748b',
                    marginTop: '0.5rem',
                    maxWidth: '24rem',
                    textAlign: 'center'
                }}>
                    The voice engine is ready and waiting for incoming traffic. Calls will appear here in real-time.
                </p>
                <button
                    onClick={async () => {
                        setIsRefreshing(true);
                        if (refetch) await refetch();
                        setTimeout(() => setIsRefreshing(false), 500);
                    }}
                    disabled={isRefreshing}
                    style={{
                        marginTop: '1.5rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1.25rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        backgroundColor: isFitenko ? '#20231F' : '#2563eb',
                        color: isFitenko ? '#FAF4E5' : '#ffffff',
                        border: isFitenko ? '1px solid #C8A653' : 'none',
                        cursor: isRefreshing ? 'not-allowed' : 'pointer',
                        boxShadow: '0 1px 3px rgba(32, 35, 31, 0.1)',
                        transition: 'all 0.15s ease'
                    }}
                >
                    <RefreshCcw size={15} className={isRefreshing ? "animate-spin" : ""} />
                    {isRefreshing ? 'Checking System...' : 'Refresh Status'}
                </button>
                {/* Visual indicator that polling is active without blocking UI */}
                <div style={{
                    marginTop: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.75rem',
                    color: '#0B8B5E',
                    backgroundColor: '#E6F6EE',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    border: '1px solid rgba(11, 139, 94, 0.2)'
                }}>
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    System Online & Monitoring
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-12 gap-6 h-[calc(100vh-12rem)] min-h-[600px]">
            {/* Active Calls List (Sidebar) */}
            <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
                <div className="flex items-center justify-between p-1">
                    <div className="flex items-center gap-2">
                        <span className="relative flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                        </span>
                        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                            Active Lines ({activeCalls.length})
                        </h3>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={async () => {
                            setIsRefreshing(true);
                            if (refetch) await refetch();
                            setTimeout(() => setIsRefreshing(false), 500);
                        }}
                        disabled={isRefreshing}
                        title="Refresh Active Calls"
                    >
                        <RefreshCcw size={14} className={isRefreshing ? "animate-spin" : ""} />
                    </Button>
                </div>

                <div className="flex-1 overflow-y-auto pr-2 space-y-3">
                    {activeCalls.map((call) => (
                        <div
                            key={call.id}
                            onClick={() => setSelectedCall(call.id)}
                            className={`
                                group relative overflow-hidden rounded-xl p-4 cursor-pointer transition-all duration-200 border
                                ${selectedCall === call.id
                                    ? 'border-blue-500 bg-blue-50/50 shadow-md ring-1 ring-blue-500/20'
                                    : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm'
                                }
                            `}
                        >
                            {/* Active Indicator Strip */}
                            {selectedCall === call.id && (
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500"></div>
                            )}

                            <div className="flex items-start justify-between mb-3 pl-2">
                                <div className="flex items-center gap-2.5">
                                    <div className={`p-2 rounded-full ${selectedCall === call.id ? 'bg-blue-100' : 'bg-slate-100'}`}>
                                        {getStatusIcon(call.status)}
                                    </div>
                                    <div>
                                        <p className="font-bold text-sm text-slate-900">
                                            {formatPhoneNumber(call.caller_number || call.callerNumber)}
                                        </p>
                                        <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                                            <UserCircle size={12} />
                                            <span>Unknown Caller</span>
                                        </div>
                                    </div>
                                </div>
                                <Badge variant={getSentimentVariant(call.sentiment)}>
                                    {call.sentiment || 'NEUTRAL'}
                                </Badge>
                            </div>

                            <div className="flex items-center justify-between pl-2 text-xs">
                                <div className="flex items-center gap-1.5 text-slate-600 bg-slate-100 px-2 py-1 rounded-md">
                                    <Clock size={12} />
                                    <span className="font-mono font-medium">
                                        {call.id === currentCall?.id ? formatDuration(elapsedTime) : 'Active'}
                                    </span>
                                </div>
                                <span className="text-slate-400">{new Date(call.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Call Details and Transcript (Main) */}
            <div className="col-span-12 lg:col-span-8 h-full flex flex-col">
                <Card className="h-full flex flex-col shadow-lg border-slate-200">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50/50">
                        <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg">
                                <Phone size={24} className="animate-pulse" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-slate-900">
                                    {formatPhoneNumber(currentCall.caller_number || currentCall.callerNumber)}
                                </h3>
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                    <Radio size={14} className="text-red-500 animate-pulse" />
                                    <span>Live Voice Session</span>
                                    <span className="mx-2 text-slate-300">•</span>
                                    <span>{formatDuration(elapsedTime)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Badge variant={getSentimentVariant(currentCall.sentiment)}>
                                {currentCall.sentiment || 'NEUTRAL'}
                            </Badge>
                            <Badge variant="neutral" className="uppercase">
                                {currentCall.status}
                            </Badge>
                        </div>
                    </div>

                    {/* Real-time Transcript */}
                    <div className="flex-1 overflow-hidden relative bg-slate-50">
                        <div className="h-full p-6 overflow-y-auto space-y-6">
                            {(currentCall.transcript_json?.messages || currentCall.transcript || []).map((message, index) => (
                                <div
                                    key={index}
                                    className={`flex w-full ${message.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
                                >
                                    <div
                                        className={`
                                            max-w-[80%] rounded-2xl p-4 shadow-sm relative
                                            ${message.role === 'assistant'
                                                ? 'bg-white text-slate-700 rounded-tl-none border border-slate-100'
                                                : 'bg-blue-600 text-white rounded-tr-none'
                                            }
                                        `}
                                    >
                                        <div className="flex items-center gap-2 mb-1.5 opacity-70">
                                            <span className="text-[10px] uppercase font-bold tracking-wider">
                                                {message.role === 'assistant' ? 'AI Assistant' : 'Caller'}
                                            </span>
                                            <span className="text-[10px]">
                                                {message.timestamp ? new Date(message.timestamp).toLocaleTimeString() : ''}
                                            </span>
                                        </div>
                                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                                    </div>
                                </div>
                            ))}
                            {(!currentCall.transcript_json?.messages && !currentCall.transcript) && (
                                <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-3">
                                    <div className="w-16 h-1 bg-slate-200 rounded-full animate-pulse"></div>
                                    <div className="w-10 h-1 bg-slate-200 rounded-full animate-pulse delay-75"></div>
                                    <p className="text-sm pt-2">Listening for speech...</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Bar */}
                    <div className="p-4 border-t border-slate-100 bg-white flex justify-between items-center gap-3">
                        <Button
                            variant="outline"
                            onClick={() => window.open(`/api/v1/voice/listen/${currentCall?.id}`, '_blank')}
                            className="flex items-center gap-2"
                        >
                            <Volume2 size={16} />
                            Listen Live
                        </Button>

                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    const phone = prompt("Enter number to transfer to:", "+1");
                                    if (phone && currentCall?.id) transferCall(currentCall.id, phone);
                                }}
                            >
                                Transfer Call
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => currentCall?.id && terminateCall(currentCall.id)}
                                className="flex items-center gap-2"
                            >
                                <PhoneOff size={16} />
                                Force Disconnect
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default LiveCallMonitor;
